import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { useToasts } from '../../lib/toast';
import type { CardReaderRow } from '@taj/types';

/** Postes proposés à l'appairage (mêmes noms que les postes d'impression). */
const SUGGESTED = ['Caisse', 'Borne 1', 'Borne 2', 'Comptoir'];

const STATUS_LABEL: Record<string, { txt: string; color: string }> = {
  paired: { txt: '🟢 Connecté', color: '#1f9150' },
  processing: { txt: '🟠 Paiement en cours', color: '#C8941E' },
  expired: { txt: '🔴 Déconnecté — à ré-appairer', color: '#c0392b' },
  offline: { txt: '🔴 Hors ligne', color: '#c0392b' },
  unknown: { txt: '⚪ État inconnu', color: '#5f5642' },
};

export function ReadersManager({ onBack }: { onBack: () => void }) {
  const push = useToasts((s) => s.push);
  const [rows, setRows] = useState<CardReaderRow[]>([]);
  const [configured, setConfigured] = useState(true);
  const [station, setStation] = useState(SUGGESTED[0]);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.listCardReaders().then(setRows).catch((e) => push(e.message, 'warn'));
    api.terminalAvailability().then((a) => setConfigured(a.configured)).catch(() => {});
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pair = async () => {
    if (!station.trim()) return push('Choisis le poste du terminal', 'warn');
    if (!code.trim()) return push('Saisis le code affiché sur le terminal', 'warn');
    setBusy(true);
    try {
      await api.pairCardReader(station.trim(), code.trim());
      push(`✅ Terminal rattaché au poste « ${station.trim()} »`);
      setCode('');
      load();
    } catch (e: any) { push(e.message, 'warn'); }
    finally { setBusy(false); }
  };

  const toggle = async (row: CardReaderRow) => {
    try { await api.setCardReaderActive(row.station, !row.active); load(); }
    catch (e: any) { push(e.message, 'warn'); }
  };

  const remove = async (row: CardReaderRow) => {
    if (!confirm(`Retirer le terminal du poste « ${row.station} » ?\nIl faudra un nouveau code d’appairage pour le remettre.`)) return;
    try { await api.unpairCardReader(row.station); push(`🗑️ Terminal « ${row.station} » retiré`); load(); }
    catch (e: any) { push(e.message, 'warn'); }
  };

  return (
    <div className="staff-scroll">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '4px 0 14px' }}>
        <button className="km-back" onClick={onBack}>←</button>
        <div className="staff-title" style={{ fontSize: '1.4rem', flex: 1 }}>💳 Terminaux de paiement</div>
      </div>
      <div className="staff-subtitle" style={{ margin: '0 2px 14px' }}>
        Un lecteur de carte <b>SumUp Solo</b> par poste. Quand le client choisit « Carte », le montant
        s’affiche tout seul sur le terminal du poste et la commande n’est validée qu’une fois la carte acceptée.
        <br />⚠️ Le lecteur <b>SumUp Air</b> (Bluetooth, sans écran) ne peut pas être piloté — il faut un <b>Solo</b>.
      </div>

      {!configured && (
        <div className="cc" style={{ marginBottom: 12, borderColor: '#e0b4b4', background: '#fdf4f4' }}>
          <div style={{ fontWeight: 800, color: '#c0392b', marginBottom: 6 }}>⚠️ Clés SumUp absentes du serveur</div>
          <div style={{ fontSize: '.85rem', color: 'var(--ink-dim)', lineHeight: 1.5 }}>
            L’appairage et les paiements resteront impossibles tant que la <b>clé API SumUp</b> et le
            <b> code marchand</b> ne sont pas installés sur le serveur. Transmets-les à ton prestataire
            (ils se trouvent dans ton compte SumUp, rubrique Développeurs).
          </div>
        </div>
      )}

      {/* ── Appairer un nouveau terminal ── */}
      <div className="cc" style={{ marginBottom: 14 }}>
        <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: '1.3rem', marginBottom: 4 }}>➕ Rattacher un terminal</div>
        <div style={{ fontSize: '.8rem', color: 'var(--ink-dim)', lineHeight: 1.5, marginBottom: 12 }}>
          Sur le terminal SumUp : <b>Menu → Connexions → API → Connecter</b>. Un code s’affiche —
          recopie-le ici. ⏱️ Il n’est valable que <b>5 minutes</b>.
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 180px' }}>
            <div style={{ fontSize: '.68rem', fontWeight: 800, color: 'var(--ink-dim)', textTransform: 'uppercase', marginBottom: 5 }}>Poste</div>
            <input
              className="inp"
              list="reader-stations"
              placeholder="Borne 1"
              value={station}
              onChange={(e) => setStation(e.target.value)}
            />
            <datalist id="reader-stations">
              {SUGGESTED.map((s) => <option key={s} value={s} />)}
            </datalist>
          </div>
          <div style={{ flex: '1 1 180px' }}>
            <div style={{ fontSize: '.68rem', fontWeight: 800, color: 'var(--ink-dim)', textTransform: 'uppercase', marginBottom: 5 }}>Code d’appairage</div>
            <input
              className="inp"
              placeholder="ABC12345"
              value={code}
              autoCapitalize="characters"
              onChange={(e) => setCode(e.target.value.toUpperCase())}
            />
          </div>
        </div>
        <button className="btn-gold" style={{ width: '100%', marginTop: 12, fontSize: '1rem', padding: '11px' }} onClick={pair} disabled={busy}>
          {busy ? 'Connexion au terminal…' : '🔗 Rattacher le terminal'}
        </button>
      </div>

      {/* ── Terminaux rattachés ── */}
      {rows.map((row) => {
        const st = STATUS_LABEL[row.status ?? 'unknown'] ?? STATUS_LABEL.unknown;
        return (
          <div key={row.station} className="cc" style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: '1.3rem', flex: 1 }}>💳 {row.station}</div>
              <div className="al-toggle" style={{ padding: '6px 10px', margin: 0, gap: 8 }}>
                <span className="an" style={{ fontSize: '.78rem' }}>{row.active ? 'Actif' : 'Inactif'}</span>
                <div className={`al-sw${row.active ? ' on' : ''}`} onClick={() => toggle(row)} />
              </div>
              <button
                className="cf-reprint"
                style={{ margin: '0 0 0 8px', padding: '6px 10px', color: '#b23', borderColor: '#e0b4b4' }}
                title="Retirer ce terminal"
                onClick={() => remove(row)}
              >🗑️</button>
            </div>
            <div style={{ fontSize: '.85rem', color: st.color, fontWeight: 700 }}>{st.txt}</div>
            <div style={{ fontSize: '.75rem', color: 'var(--ink-faint)', marginTop: 4 }}>
              {row.name}
              {row.model === 'virtual-solo' && ' · 🧪 terminal de test (aucun argent réel)'}
            </div>
          </div>
        );
      })}
      {rows.length === 0 && <div className="no-data">Aucun terminal rattaché pour l’instant.</div>}
    </div>
  );
}
